import { MaintenanceModel, MaintenancePaymentModel } from 'api/models';
import Collapsible from 'components/Collapsible/Collapsible';
import UserActionMaintenance from 'containercomponents/MaintenanceStatus/UserActionMaintenance/UserActionMaintenance';

/**
 * The three branches below return different shapes — two spreads of a profile
 * and a bare `{}` for anything paid by Belong — so the inferred union has no
 * common members. This names the four fields the caller actually reads, all
 * optional, which the empty branch satisfies unchanged.
 */
type PaymentUser = {
  userId?: string;
  name?: string;
  profileImageUrl?: string;
  reportedBy?: string;
};

const getUserFromPayment = (
  task: MaintenanceModel,
  payment: MaintenancePaymentModel,
): PaymentUser => {
  if (payment.paidBy === 'Homeowner') {
    const newTask = {
      ...task.homeowner,
      reportedBy: 'Homeowner',
    };
    return newTask;
  }

  if (payment.paidBy === 'Resident') {
    const newTask = {
      ...task.resident,
      ...payment.paidByUser,
      reportedBy: 'Resident',
    };
    return newTask;
  }

  return {};
};

const getTextFromConsentStatus = (
  approval: MaintenancePaymentModel['consentStatus'],
) => {
  if (approval === 'Approved' || approval === 'NotRequired') {
    return 'Approved By';
  }

  if (approval === 'Rejected') {
    return 'Not Approved By';
  }

  if (approval === 'Required') {
    return 'Pending Approval';
  }

  return 'Pending';
};

const getTextColorFromConsentStatus = (
  approval: MaintenancePaymentModel['consentStatus'],
) => {
  let color = '#ff9c00';
  if (approval === 'Approved' || approval === 'NotRequired') {
    color = '#325e77';
  }

  if (approval === 'Rejected') {
    color = '#ff5661';
  }

  if (approval === 'Required') {
    color = '#ff9c00';
  }

  return { color };
};

type MaintenanceApprovalAndResponsibilityProps = {
  task: MaintenanceModel;
  payments: MaintenancePaymentModel[];
  collapsable?: boolean;
  title: string;
  hideConsentStatus?: boolean;
};

export const MaintenanceApprovalAndResponsibility = ({
  task,
  payments,
  collapsable = true,
  title,
  hideConsentStatus = false,
}: MaintenanceApprovalAndResponsibilityProps) => {
  if (collapsable) {
    return (
      <Collapsible
        title={title}
        collapsed={false}
        inner={
          <>
            {payments.map((payment) => {
              const user = getUserFromPayment(task, payment);

              return (
                <UserActionMaintenance
                  key={user.userId}
                  reportedBy={user.reportedBy ? user.reportedBy : 'Belong'}
                  name={user.name}
                  time={payment.consentOn}
                  profileImage={user.profileImageUrl}
                  userId={user.userId}
                  taskType={
                    hideConsentStatus
                      ? 'Pending'
                      : getTextFromConsentStatus(payment.consentStatus)
                  }
                  taskTypeStyle={
                    hideConsentStatus
                      ? { color: '#ff9c00' }
                      : getTextColorFromConsentStatus(payment.consentStatus)
                  }
                  disableProfileClick
                />
              );
            })}
          </>
        }
      />
    );
  }

  return (
    <>
      {payments.map((payment) => {
        const user = getUserFromPayment(task, payment);

        return (
          <UserActionMaintenance
            key={user.userId}
            reportedBy={user.reportedBy ? user.reportedBy : 'Belong'}
            name={user.name}
            time={payment.consentOn}
            profileImage={user.profileImageUrl}
            userId={user.userId}
            taskType={
              hideConsentStatus
                ? 'Pending'
                : getTextFromConsentStatus(payment.consentStatus)
            }
            taskTypeStyle={
              hideConsentStatus
                ? { color: '#ff9c00' }
                : getTextColorFromConsentStatus(payment.consentStatus)
            }
            disableProfileClick
          />
        );
      })}
    </>
  );
};
